import {
  parseQuickSwitcherScopedQuery,
  type QuickSearchScope,
} from "../../lib/quickSwitcherScopedQuery.ts";

import type { SuntronicInvoiceShipmentRow } from "./suntronicInvoicesApi";

function joinSearchParts(parts: Array<string | number | null | undefined>): string {
  return parts
    .filter((value) => value != null && String(value).trim() !== "")
    .join(" ")
    .toLowerCase();
}

function shipmentHaystackForScope(row: SuntronicInvoiceShipmentRow, scope: QuickSearchScope): string {
  switch (scope) {
    case "batch":
      return row.batch_id == null ? "" : String(row.batch_id);
    case "shipment":
      return String(row.shipment_id);
    case "sku":
      return (row.sku ?? "").trim().toLowerCase();
    case "po":
      return "";
    case "all":
    default:
      return joinSearchParts([
        row.shipment_id,
        row.batch_id,
        row.sku,
        row.location_name,
      ]);
  }
}

function effectiveShipmentSearchScope(scope: QuickSearchScope): QuickSearchScope {
  if (scope === "page" || scope === "wiki" || scope === "field" || scope === "so" || scope === "end-user") {
    return "all";
  }
  return scope;
}

export function matchesSuntronicInvoiceShipmentSearch(
  row: SuntronicInvoiceShipmentRow,
  rawQuery: string,
): boolean {
  const parsed = parseQuickSwitcherScopedQuery(rawQuery);
  const term = parsed.term.trim().toLowerCase();
  const scope = effectiveShipmentSearchScope(parsed.scope);
  if (term === "") {
    return scope === "all";
  }
  if (scope === "batch") {
    return row.batch_id != null && String(row.batch_id).toLowerCase() === term;
  }
  if (scope === "shipment") {
    return String(row.shipment_id).toLowerCase() === term;
  }
  const haystack = shipmentHaystackForScope(row, scope);
  if (haystack === "") {
    return false;
  }
  return haystack.includes(term);
}

export function filterSuntronicInvoiceShipmentRows(
  rows: readonly SuntronicInvoiceShipmentRow[],
  rawQuery: string,
): SuntronicInvoiceShipmentRow[] {
  const trimmed = rawQuery.trim();
  if (trimmed === "") {
    return [...rows];
  }
  return rows.filter((row) => matchesSuntronicInvoiceShipmentSearch(row, trimmed));
}

export type SuntronicShipmentSortKey =
  | "batch"
  | "shipment"
  | "location"
  | "sku"
  | "dateShipped"
  | "shipmentQty"
  | "matchedQty"
  | "remainingQty";

function toSortNumber(value: number | string | null | undefined): number {
  if (value == null || value === "") return Number.NEGATIVE_INFINITY;
  const parsed = typeof value === "number" ? value : Number.parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
}

export function compareSuntronicInvoiceShipments(
  left: SuntronicInvoiceShipmentRow,
  right: SuntronicInvoiceShipmentRow,
  key: SuntronicShipmentSortKey,
  dir: "asc" | "desc",
): number {
  const direction = dir === "asc" ? 1 : -1;
  switch (key) {
    case "shipment":
      return (toSortNumber(left.shipment_id) - toSortNumber(right.shipment_id)) * direction;
    case "location":
      return (left.location_name ?? "").localeCompare(right.location_name ?? "") * direction;
    case "sku":
      return (left.sku ?? "").localeCompare(right.sku ?? "") * direction;
    case "dateShipped": {
      const leftTime = Date.parse(left.date_shipped ?? "");
      const rightTime = Date.parse(right.date_shipped ?? "");
      const leftValid = Number.isFinite(leftTime);
      const rightValid = Number.isFinite(rightTime);
      if (leftValid && rightValid && leftTime !== rightTime) {
        return (leftTime - rightTime) * direction;
      }
      return String(left.date_shipped ?? "").localeCompare(String(right.date_shipped ?? "")) * direction;
    }
    case "shipmentQty":
      return (toSortNumber(left.shipment_quantity) - toSortNumber(right.shipment_quantity)) * direction;
    case "matchedQty":
      return (toSortNumber(left.total_matched_quantity) - toSortNumber(right.total_matched_quantity)) * direction;
    case "remainingQty":
      return (toSortNumber(left.remaining_quantity) - toSortNumber(right.remaining_quantity)) * direction;
    case "batch":
    default:
      return (toSortNumber(left.batch_id) - toSortNumber(right.batch_id)) * direction;
  }
}
